import React, { useState, useMemo, useRef, useEffect } from "react";
import ReactECharts from "echarts-for-react";
import ChartToolbar from "./ChartToolbar/ChartToolbar";
import * as echarts from "echarts";
import { secondsToHHMM } from "../Utilitie";
import KpiCardForGraphs from "../CHARTS/KpiCardForGraphs/KpiCardForGraphs";

const GroupedTrendPerCategory = ({ data, limit = 15, sortMode = "desc" }) => {
  const [currentLimit, setCurrentLimit] = useState(limit);
  const [currentSort, setCurrentSort] = useState(sortMode);
  const [activeLegends, setActiveLegends] = useState(null);
  const [customTrendData, setCustomTrendData] = useState(null);
  const [updatedData, setUpdatedData] = useState(data);
  const [chartType, setChartType] = useState("bar");
  const chartRef = useRef(null);
  const [showValues, setShowValues] = useState(false);

  const initialLimitRef = useRef(limit);
  const initialSortRef = useRef(sortMode);
  useEffect(() => {
    const chart = chartRef.current?.getEchartsInstance();
    if (!chart) return;

    const handler = (params) => {
      // get visible series names
      const visibleSeries = Object.keys(params.selected).filter(
        (key) => params.selected[key] === true
      );
      setActiveLegends(visibleSeries.filter((l) => l != "Trend"));
      setUpdatedData(data.filter((o) => visibleSeries.includes(o.A_MONTH)));
    };

    chart.on("legendselectchanged", handler);

    return () => chart.off("legendselectchanged", handler);
  }, [data]);
  // ---------------------------------------------------------------
  const handleChartType = (type) => {
    if (type === "reset") {
      setChartType("bar");
      return;
    }
    setChartType(type);
  };
  // ---------------- DOWNLOAD PNG ----------------
  const downloadChart = () => {
    if (!chartRef.current) return;
    const instance = chartRef.current.getEchartsInstance();

    const imgData = instance.getDataURL({
      type: "png",
      pixelRatio: 2,
      backgroundColor: "#fff",
    });

    const link = document.createElement("a");
    link.href = imgData;
    link.download = `Sub_Cause_Trend_Chart.png`;
    link.click();
  };

  // ---------------- SORT SUB CAUSES ----------------
  const sortedLimitedCauses = useMemo(() => {
    if (!data || data.length === 0) return [];

    const subCauses = [...new Set(data.map((o) => o.SUB_CAUSE))];

    subCauses.sort((a, b) => {
      const sumA = data
        .filter((o) => o.SUB_CAUSE === a)
        .reduce(
          (s, r) =>
            s + Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0),
          0
        );

      const sumB = data
        .filter((o) => o.SUB_CAUSE === b)
        .reduce(
          (s, r) =>
            s + Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0),
          0
        );

      return currentSort === "desc" ? sumB - sumA : sumA - sumB;
    });

    return subCauses.slice(0, currentLimit);
  }, [data, currentLimit, currentSort]);

  // ---------------- SORT MONTHS ----------------
  const months = useMemo(() => {
    if (!data || data.length === 0) return [];

    return [...new Set(data.map((o) => o.A_MONTH))].sort(
      (a, b) => new Date(`01-${a}-2024`) - new Date(`01-${b}-2024`)
    );
  }, [data]);
  useEffect(() => {
    setActiveLegends(months);
  }, [data.length]);

  useEffect(() => {
    setUpdatedData(data?.filter((o) => activeLegends?.includes(o.A_MONTH)));
  }, [data?.length, activeLegends?.length]);
  const totalSubCauses = useMemo(() => {
    return new Set(updatedData.map((o) => o.SUB_CAUSE)).size;
  }, [updatedData.length]);
  // console.log(data);
  const totalDurationHHMM = useMemo(() => {
    const s = updatedData.reduce(
      (a, b) => a + Number(b.Average_Detention_Time_Mins_Total_20 * 60 || 0),
      0
    );
    return secondsToHHMM(s);
  }, [updatedData.length]);

  const { topCauseName, topCauseValue } = useMemo(() => {
    const totals = {};
    updatedData.forEach((r) => {
      totals[r.SUB_CAUSE] =
        (totals[r.SUB_CAUSE] || 0) +
        Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0);
    });

    const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    const [name, sec] = sorted[0] || ["-", 0];

    return {
      topCauseName: name,
      topCauseValue: secondsToHHMM(sec),
    };
  }, [updatedData.length]);

  const overallMonthlyAvgHHMM = useMemo(() => {
    const months = [...new Set(updatedData.map((o) => o.A_MONTH))];
    const total = updatedData.reduce(
      (s, r) => s + Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0),
      0
    );
    return secondsToHHMM(total / (months.length || 1));
  }, [updatedData.length]);

  const topBySorting = useMemo(() => {
    return sortedLimitedCauses?.[0] || "-";
  }, [sortedLimitedCauses]);

  const { latestMonth, latestMonthHHMM } = useMemo(() => {
    const monthsSorted = [...new Set(updatedData.map((o) => o.A_MONTH))].sort(
      (a, b) => new Date(`01-${a}-2024`) - new Date(`01-${b}-2024`)
    );

    const lastMonth = monthsSorted.at(-1);
    const total = updatedData
      .filter((o) => o.A_MONTH === lastMonth)
      .reduce(
        (s, r) => s + Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0),
        0
      );

    return {
      latestMonth: lastMonth,
      latestMonthHHMM: secondsToHHMM(total),
    };
  }, [updatedData.length]);

  // ---------------- CUSTOM TREND ----------------
  useEffect(() => {
    if (activeLegends && activeLegends.length > 0) {
      const tempCustomTrendData = sortedLimitedCauses.map((sc, idx) => {
        const values =
          activeLegends &&
          activeLegends.map((m) => {
            const rows = data.filter(
              (o) => o.SUB_CAUSE === sc && o.A_MONTH === m
            );
            return rows.reduce(
              (s, r) =>
                s + Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0),
              0
            );
          });

        return [idx, ...values];
      });
      setCustomTrendData(tempCustomTrendData);
    }
  }, [activeLegends]);

  if (!data || data.length === 0)
    return <div style={{ padding: 20 }}>No Data Found</div>;

  const COLORS = [
    "#a322a3d2",
    "#f38313ff",
    "#e44649ff",
    "#3371a3d5",
    "#59A14F",
    "#EDC948",
    "#865a7ac5",
    "#FF9DA7",
  ];

  // ---------------- BAR / LINE SERIES ----------------
  const barSeries = months.map((month, i) => ({
    name: month,
    type: chartType === "line" ? "line" : "bar",
    smooth: true,
    barGap: "20%",
    barCategoryGap: "30%",

    itemStyle: {
      color: COLORS[i % COLORS.length],
      borderRadius: chartType === "bar" ? [4, 4, 0, 0] : 0,
      shadowColor: "rgba(0, 0, 0, 0.18)",
      shadowBlur: 12,
      shadowOffsetX: 2,
      shadowOffsetY: 3,
    },

    label: {
      show: showValues,
      position: "top",
      formatter: (p) => secondsToHHMM(p.value),
      fontSize: 9,
      fontWeight: "bold",
      rotate: chartType === "line" ? 0 : 90,
      offset: chartType === "line" ? [0, 0] : [0, 0],
      align: "center",
      verticalAlign: "bottom",
    },

    labelLayout: (params) => {
      if (chartType === "line") {
        return {
          x: params.rect.x,
          y: params.rect.y - 12,
          align: "center",
          verticalAlign: "bottom",
        };
      }
      return {
        x: params.rect.x + params.rect.width,
        y: params.rect.y - 30,
        align: "center",
        verticalAlign: "bottom",
      };
    },

    data: sortedLimitedCauses.map((sc) => {
      const rows = data.filter(
        (o) => o.SUB_CAUSE === sc && o.A_MONTH === month
      );
      return rows.reduce(
        (s, r) => s + Number(r.Average_Detention_Time_Mins_Total_20 * 60 || 0),
        0
      );
    }),
  }));

  // ---------------- ECHART OPTION ----------------
  const option = {
    tooltip: {
      trigger: "axis",
      formatter: function (params) {
        const subCause = params[0].axisValue;

        let monthValues = [];
        let monthLines = [];

        params.forEach((p) => {
          if (p.seriesName !== "Trend") {
            const sec = Number(p.value) || 0;
            const converted = secondsToHHMM(sec);
            monthValues.push(sec);
            monthLines.push(
              `<span style="color:${p.color}">●</span> ${p.seriesName}: <b>${converted}</b>`
            );
          }
        });

        // ---------- Trend Avg----------
        let trendAvgSec = 0;
        if (monthValues.length > 0) {
          const sum = monthValues.reduce((a, b) => a + b, 0);
          trendAvgSec = Math.round(sum / monthValues.length);
        }

        const trendAvgHHMM = secondsToHHMM(trendAvgSec);

        return `
      <div style="padding:2px 4px;">
        <b>${subCause}</b><br/>
        
        <span style="color:#4F46E5">●</span> 
        Trend Avg: <b>${trendAvgHHMM}</b><br/><br/>
        
        ${monthLines.join("<br/>")}
      </div>
    `;
      },
    },

    legend: { top: 10 },
    grid: { left: "5%", right: "5%", top: 60, bottom: 80, containLabel: true },

    dataZoom: [
      { type: "slider", xAxisIndex: 0 },
      { type: "inside", xAxisIndex: 0, start: 0, end: 50, height: 15 },
    ],

    xAxis: {
      type: "category",
      data: sortedLimitedCauses,
      axisLabel: { rotate: 40, fontSize: 9, fontWeight: 600 },
    },

    yAxis: { type: "value" },

    series:
      chartType === "reset"
        ? barSeries
        : [
            {
              id: "trend-line",
              name: "Trend",
              type: "custom",
              z: 20,
              data: customTrendData,

              renderItem(params, api) {
                const xIndex = api.value(0);

                const indices = api.currentSeriesIndices();

                const barCount = indices.length - 1;

                const layout = api.barLayout({
                  barGap: "20%",
                  barCategoryGap: "30%",
                  count: barCount,
                });

                const points = [];

                for (let i = 0; i < barCount; i++) {
                  const v = api.value(i + 1);

                  const coord = api.coord([xIndex, v]);
                  coord[0] += layout[i].offsetCenter;
                  coord[1] -= 12;

                  points.push(coord);
                }

                return {
                  type: "polyline",
                  shape: { points },
                  style: {
                    stroke: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                      { offset: 0, color: "#4F46E5" },
                      { offset: 1, color: "#A78BFA" },
                    ]),
                    lineWidth: 3,
                    shadowColor: "rgba(79,70,229,0.35)",
                    shadowBlur: 12,
                    shadowOffsetY: 4,
                    fill: "none",
                  },
                };
              },
            },

            ...barSeries.map((s, i) => ({
              ...s,
              id: `bar-${i}`,
            })),
          ],
  };

  // ---------------- UI ----------------
  return (
    <div className="">
      <div className="row">
        <div className="col-md">
          <div className="papaDiv" style={{ background: "rgba(0,0,0,0)" }}>
            <KpiCardForGraphs
              items={[
                {
                  title: "Total Sub-Causes",
                  value: totalSubCauses,
                  iconType: "Total Sub-Causes",
                  color: "#4a90e2",
                },
                {
                  title: "Total Duration",
                  value: totalDurationHHMM,
                  iconType: "Total Duration",
                  color: "#ff7f50",
                },
                {
                  title: "Top Sub-Cause",
                  value: `${topCauseName} (${topCauseValue})`,
                  iconType: "Top Sub-Cause",
                  color: "#00b894",
                  bg: "#f0f7ff",
                },
                {
                  title: "Monthly Avg",
                  value: overallMonthlyAvgHHMM,
                  iconType: "Monthly Avg",
                },
                {
                  title: "Top (Sort Applied)",
                  value: topBySorting,
                  iconType: "Top (Sort Applied)",
                },
                {
                  title: `Total in ${latestMonth} (Current Month)`,
                  value: latestMonthHHMM,
                  iconType: `Current Month Total`,
                },
              ]}
            />

            {/* ---------- HEADER ROW (Toolbar + Buttons) ---------- */}
            <div className="bg-dark text-white p-1 d-flex justify-content-between align-items-center">
              {/* Title */}
              <h5 className="m-0">
                Sub-Cause Trend Chart
                <span style={{ color: "rgba(189,188,188,1)" }}>
                  {" "}
                  (Month wise)
                </span>
              </h5>

              {/* Right side: Toolbar + Buttons */}
              <div className="d-flex align-items-center gap-2">
                <ChartToolbar
                  chartRef={chartRef}
                  chartType={chartType}
                  setChartType={setChartType}
                  limit={currentLimit}
                  onLimitChange={(v) => {
                    if (v === "" || v === null) {
                      setCurrentLimit("");
                    } else {
                      setCurrentLimit(Number(v));
                    }
                  }}
                  onTop10={() => setCurrentLimit(10)}
                  onTop20={() => setCurrentLimit(20)}
                  onShowAll={() =>
                    setCurrentLimit(
                      [...new Set(data.map((o) => o.SUB_CAUSE))].length
                    )
                  }
                  onSortHighest={() => setCurrentSort("desc")}
                  onSortLowest={() => setCurrentSort("asc")}
                  onClear={(val) => {
                    if (val === "limit") {
                      setCurrentLimit(initialLimitRef.current);
                    } else if (val === "all") {
                      setCurrentLimit(initialLimitRef.current);
                      setCurrentSort(initialSortRef.current);
                    }
                  }}
                  onChartType={handleChartType}
                  onDownload={downloadChart}
                  onToggleValues={() => setShowValues((prev) => !prev)}
                  valuesEnabled={showValues}
                />
              </div>
            </div>
            {/* ---------- CHART ---------- */}
            <div style={{ height: "600px" }}>
              <ReactECharts
                // replaceMerge={["series"]}
                ref={chartRef}
                option={option}
                style={{ height: "100%", width: "100%" }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroupedTrendPerCategory;
